/**
 * @file ConversationArea.tsx
 * @description Переписка выбранного диалога и сведения о нём
 * (chat-window-design.md §1б–д): шапка, полоса «Диалог передан вам»,
 * лента, поле ввода; в три зоны — панель сведений справа, в две зоны на
 * весь экран — шторкой «Сведения». Окна «Передать» и «Вернуть оператору».
 */

import { Button } from "../ui/button";
import { ListError } from "../ui/ListStates";
import { NoticeStrip } from "../ui/NoticeStrip";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { chatMenuActions } from "./chat-actions";
import { compactOrderNumber, customerName, whenPhrase } from "./chat-format";
import { CHAT_TEXT } from "./chat-texts";
import { ConversationFeed } from "./ConversationFeed";
import { ConversationHeader } from "./ConversationHeader";
import { ConversationInfoPanel } from "./ConversationInfoPanel";
import { ConversationMenu } from "./ConversationMenu";
import { countServiceRecords } from "./feed-model";
import { MessageComposer } from "./MessageComposer";
import { ReturnToQueueConfirm } from "./ReturnToQueueConfirm";
import { TransferPicker } from "./TransferPicker";
import { useConversationController } from "./useConversationController";

interface Props {
  id: string;
  /** Имя клиента из строки списка — шапка до загрузки карточки. */
  fallbackName: string;
}

export function ConversationArea({ id, fallbackName }: Props) {
  const c = useConversationController(id);
  const { chat, conv, actions } = c;
  const { detail } = conv;
  const phone = chat.viewport === "phone";
  const zones = chat.presentation.zones;
  const wideFeed = chat.presentation.frame === "full" && !phone;

  const menuItems = detail
    ? chatMenuActions(detail, { canOpenOrders: chat.canOpenOrders, canOpenCustomers: chat.canOpenCustomers, serviceInMenu: !wideFeed, serviceShown: c.serviceShown })
    : [];
  const caption = detail
    ? [customerName(detail.customer), detail.contextOrder ? compactOrderNumber(detail.contextOrder.number) : CHAT_TEXT.generalChat].join(" · ")
    : fallbackName;

  const infoPanel = detail ? (
    <ConversationInfoPanel
      detail={detail}
      feed={conv.feed}
      meUserId={chat.userId}
      busy={actions.busy !== null}
      onTake={() => void actions.take()}
      onTransfer={() => c.setTransferOpen(true)}
      onOpenOrder={c.openOrder}
      onOpenCustomer={c.openCustomer}
      onFocusEvent={(eventId) => {
        c.setInfoOpen(false);
        c.focusOn(eventId);
      }}
    />
  ) : null;

  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <section aria-label={CHAT_TEXT.feedLabel} className="flex min-h-0 min-w-0 flex-1 flex-col">
        <ConversationHeader
          detail={detail}
          fallbackName={fallbackName}
          meUserId={chat.userId}
          phone={phone}
          showToolbar={wideFeed}
          showInfoButton={zones === 2 && wideFeed && detail !== null}
          serviceShown={c.serviceShown}
          serviceCount={countServiceRecords(conv.feed)}
          busy={actions.busy !== null}
          menu={<ConversationMenu items={menuItems} phone={phone} caption={caption} onRun={c.runMenu} />}
          onBack={chat.back}
          onTake={() => void actions.take()}
          onUnarchive={() => void actions.unarchive()}
          onToggleService={c.toggleService}
          onOpenInfo={() => c.setInfoOpen(true)}
          onOpenOrder={c.openOrder}
        />
        {c.transferredEvent ? (
          <NoticeStrip
            tone="info"
            className="m-2"
            action={
              <Button variant="outline" size="sm" className="min-h-12 lg:min-h-8" onClick={c.dismissBanner}>
                {CHAT_TEXT.understood}
              </Button>
            }
          >
            {CHAT_TEXT.transferredBanner(c.transferredEvent.actor?.name ?? "сотрудник", whenPhrase(c.transferredEvent.at))}
          </NoticeStrip>
        ) : null}
        {conv.error ? (
          <div className="p-3">
            <ListError message={CHAT_TEXT.conversationError} onRetry={conv.retry} retryLabel={CHAT_TEXT.retry} />
          </div>
        ) : (
          <ConversationFeed
            key={id}
            items={conv.feed}
            hasEarlier={conv.hasEarlier}
            loadingEarlier={conv.loadingEarlier}
            onLoadEarlier={conv.loadEarlier}
            pending={c.sender.pending}
            loading={conv.loading}
            firstUnreadId={conv.firstUnreadId}
            serviceShown={c.serviceShown}
            highlightEventId={c.transferredEventId}
            focusEvent={c.focusEvent}
            wide={wideFeed}
            onRetry={c.sender.retry}
            onRemove={c.sender.remove}
          />
        )}
        <MessageComposer
          detail={detail}
          meUserId={chat.userId}
          lost={conv.lost}
          shopId={chat.shopId}
          conversationId={id}
          restore={c.restoreDraft}
          onSend={c.sender.send}
          busy={actions.busy !== null}
          onTake={() => void actions.take()}
          onUnarchive={() => void actions.unarchive()}
          onToList={() => {
            chat.bumpData();
            chat.back();
          }}
          autoFocus={!phone}
        />
      </section>

      {zones === 3 && infoPanel ? (
        <aside aria-label={CHAT_TEXT.info} className="flex w-80 shrink-0 flex-col border-l border-outline-variant min-[1600px]:w-90">
          {infoPanel}
        </aside>
      ) : null}

      <Sheet open={c.infoOpen && zones < 3} onOpenChange={c.setInfoOpen}>
        <SheetContent side="right" className="w-[360px] max-w-[100vw] gap-0 p-0 sm:max-w-[360px]">
          <SheetHeader className="px-4 pb-2 pt-4">
            <SheetTitle>{CHAT_TEXT.info}</SheetTitle>
          </SheetHeader>
          {infoPanel}
        </SheetContent>
      </Sheet>

      {detail ? (
        <TransferPicker
          open={c.transferOpen}
          conversationId={detail.id}
          phone={phone}
          busy={actions.busy === "transfer"}
          onClose={() => c.setTransferOpen(false)}
          onTransfer={actions.transfer}
        />
      ) : null}
      <ReturnToQueueConfirm
        open={c.confirmReturn}
        busy={actions.busy === "returnToQueue"}
        onClose={() => c.setConfirmReturn(false)}
        onConfirm={() => void actions.returnToQueue().then(() => c.setConfirmReturn(false))}
      />
    </div>
  );
}
